import { useStore } from '@nanostores/solid'
import { createEffect, createSignal, Show } from 'solid-js'
import closeSvg from '~/assets/boosting-banner/close.svg'
import dots1Svg from '~/assets/boosting-banner/dots1.svg'
import dots2Svg from '~/assets/boosting-banner/dots2.svg'
import ellipse3Svg from '~/assets/boosting-banner/ellipse3.svg'
import objectPng from '~/assets/boosting-sheet/gift-mega-v3.png'
import reflection1Svg from '~/assets/boosting-banner/reflection-1.svg'
import reflection2Svg from '~/assets/boosting-banner/reflection-2.svg'
import starSvg from '~/assets/boosting-banner/star.svg'
import { m } from '~/paraglide/messages'
import {
  $isBoostingBannerDismissed,
  $isBoostingBannerExiting,
  BOOSTING_BANNER_EXIT_FLIGHT_MS,
  startBoostingBannerExit,
} from '~/store/boosting-banner'
import {
  $boostingBannerFlyOnSheetClose,
  $isBoostingPacksOpen,
  openBoostingPacks,
} from '~/store/boosting-packs'
import { $pageRevealed } from '~/store/page-revealed'

/** Delay between the sheet finishing its close and the banner starting its
 *  fly-to-chip flight. Matches the drawer's ~300ms slide-down so the overlay
 *  has cleared and the resting banner is fully visible before it takes off. */
const SHEET_CLOSE_HANDOFF_DELAY_MS = 300

/** Bottom-fixed "Boosting packs are here!" banner. 1:1 port of Figma node
 *  4433:34864. Stack (back → front):
 *   (a) 197px vertical bg gradient transparent → #b870d0
 *   (b) Frosted purple wash + 4.7px backdrop blur
 *   (c) Two diagonal reflection rectangles, right ~70%, opacity 0.59
 *   (d) Gift box composite (object + shadow + ellipse glow + backlight +
 *       two dot decorations, all with their own blend modes + transforms)
 *   (e) ✦ Star + "Boosting packs are here!" message + × close button */
export function BoostingPacksBanner() {
  const dismissed = useStore($isBoostingBannerDismissed)
  const exiting = useStore($isBoostingBannerExiting)
  // Mount-gated on $pageRevealed so the banner only joins the screen after
  // EventHome's progressive disclosure has settled (~1800ms post-`started`).
  const pageRevealed = useStore($pageRevealed)
  const [exitTransform, setExitTransform] = createSignal<string>('')
  const [exitRadius, setExitRadius] = createSignal<string>('0px')
  // Initially false; flips true on the second rAF *after pageRevealed fires*.
  // Using createEffect (not onMount) because the outer BoostingPacksBanner
  // component is always mounted in EventHome — onMount would fire at page
  // load and flip revealed before $pageRevealed is true, so by the time the
  // inner Show actually renders there'd be no initial off-screen state to
  // transition from.
  const [revealed, setRevealed] = createSignal(false)
  let innerEl: HTMLDivElement | undefined

  createEffect(() => {
    if (!pageRevealed() || revealed()) return
    requestAnimationFrame(() => requestAnimationFrame(() => setRevealed(true)))
  })

  // Computes the transform that carries the banner to the top-nav chip and
  // kicks off the exit. Shared by the × button and the deferred sheet-close
  // hand-off below. No-op if already gone or animating.
  const flyToChip = () => {
    if (!innerEl || $isBoostingBannerExiting.get() || dismissed()) return

    // Target = approximate centre of the booster chip in the top nav.
    // Vertical: <header> gap-2 (8) + safe-area (≥36) + py-2 (8) + half row (21).
    // Horizontal: chip sits left of 3 currency pills (~190px) inside the
    // right-aligned cluster, with px-2 container padding + gap-1 between.
    // Currency widths vary with digit count, so this is intentionally
    // approximate — the chip's own pop-in masks small mismatches.
    const safeTopRaw = getComputedStyle(document.documentElement)
      .getPropertyValue('--safe-area-top')
      .trim()
    const safeTop = parseFloat(safeTopRaw) || 0
    const targetCenterY = Math.max(safeTop, 36) + 37
    const targetCenterX = window.innerWidth - 220
    const targetWidth = 85
    // Chip radius (25.161px) is roughly half its 26px height → pill shape.
    const chipRadius = 25.161

    const rect = innerEl.getBoundingClientRect()
    const sourceCenterX = rect.left + rect.width / 2
    const sourceCenterY = rect.top + rect.height / 2
    const dx = targetCenterX - sourceCenterX
    const dy = targetCenterY - sourceCenterY
    const scale = Math.max(0.1, targetWidth / rect.width)
    // Pre-scale radius so the *visible* corner radius lands on chipRadius
    // once the transform settles. (border-radius is scaled by transform.)
    const targetRadius = chipRadius / scale

    setExitTransform(`translate(${dx}px, ${dy}px) scale(${scale})`)
    setExitRadius(`${targetRadius}px`)
    startBoostingBannerExit()
  }

  const handleClose = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation()
    flyToChip()
  }

  // Deferred hand-off: when the sheet was opened straight from the banner
  // (first open), the banner stays put behind the sheet and plays its
  // fly-to-chip flight on the sheet's *close* instead of dismissing instantly.
  const packsOpen = useStore($isBoostingPacksOpen)
  let wasPacksOpen = false
  createEffect(() => {
    const open = packsOpen()
    const justClosed = wasPacksOpen && !open
    wasPacksOpen = open
    if (!justClosed || !$boostingBannerFlyOnSheetClose.get()) return
    if (dismissed() || $isBoostingBannerExiting.get()) return
    $boostingBannerFlyOnSheetClose.set(false)
    // Wait for the drawer to slide away so the flight reads against the page,
    // not through the fading overlay. Bail if the sheet reopened meanwhile.
    setTimeout(() => {
      if ($isBoostingPacksOpen.get()) return
      flyToChip()
    }, SHEET_CLOSE_HANDOFF_DELAY_MS)
  })

  return (
    <Show when={pageRevealed() && (!dismissed() || exiting())}>
      <div class="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col items-center">
        <div
          ref={innerEl}
          class="relative w-full"
          style={{
            // Transition is always declared so transform/opacity/radius
            // changes interpolate (avoids the same-frame race where a new
            // transition arrives alongside the new values and the change
            // snaps). Two phases: entrance (slide up + fade in) uses a
            // slightly longer ease-out curve; exit (fly to chip) uses the
            // 520ms cubic-bezier with opacity-delay for the hand-off.
            transition: exiting()
              ? `transform ${BOOSTING_BANNER_EXIT_FLIGHT_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${BOOSTING_BANNER_EXIT_FLIGHT_MS - 120}ms ease-in 120ms, border-radius ${BOOSTING_BANNER_EXIT_FLIGHT_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`
              : 'transform 520ms cubic-bezier(0.16, 1, 0.3, 1), opacity 520ms cubic-bezier(0.16, 1, 0.3, 1)',
            transform: exiting()
              ? exitTransform()
              : revealed()
                ? undefined
                : 'translateY(100%)',
            opacity: exiting() ? '0' : revealed() ? undefined : '0',
            // Clip the bleeding gradient/reflection to the box so the
            // rounding actually shows. Only kicks in during exit — the
            // resting banner still lets decorations overflow.
            overflow: exiting() ? 'hidden' : undefined,
            'border-radius': exiting() ? exitRadius() : '0px',
            'will-change':
              exiting() || !revealed() ? 'transform, opacity' : undefined,
          }}
        >
          {/* (a) Bottom-anchored vertical gradient — transparent at top
              6.5%, deepens to #b870d0 by 69%. Extends 197px upward so the
              fade overlaps cards above without a hard line. */}
          <div
            aria-hidden
            class="pointer-events-none absolute inset-x-0 bottom-0 h-[197px]"
            style={{
              'background-image':
                'linear-gradient(to bottom, rgba(0,0,0,0) 6.513%, #b870d0 69.328%)',
            }}
          />
          {/* (b) Frosted purple wash + 4.7px backdrop blur. */}
          <div
            aria-hidden
            class="pointer-events-none absolute inset-0 backdrop-blur-[4.7px]"
            style={{
              'background-image':
                'linear-gradient(232.92deg, rgba(237,164,255,0.2) 15.854%, rgba(123,114,242,0.2) 77.139%)',
            }}
          />
          {/* (c) Reflection group — right ~70% area, 168px tall, opacity
              0.59, top:-32px so the two rectangles slice across upper half. */}
          <div
            aria-hidden
            class="pointer-events-none absolute right-[0.27%] top-0 h-[168px] w-[70.24%] overflow-clip opacity-[0.59]"
          >
            <img
              src={reflection1Svg}
              alt=""
              aria-hidden="true"
              class="absolute left-[8.4%] right-[-10.31%] top-[-32px] aspect-[267/155.81]"
            />
            <img
              src={reflection2Svg}
              alt=""
              aria-hidden="true"
              class="absolute left-[-3.05%] right-[1.15%] top-[-32px] aspect-[267/155.81]"
            />
          </div>

          {/* Content row — max-w-[640px] centered, px-4 py-2 per Figma. */}
          <button
            type="button"
            onClick={() => {
              if (exiting()) return
              openBoostingPacks()
            }}
            class="pointer-events-auto relative mx-auto flex w-full max-w-[640px] cursor-pointer items-center justify-center gap-3 px-4 py-2 text-left transition-transform active:scale-[0.99]"
            classList={{ 'pointer-events-none': exiting() }}
          >
            {/* (d) Gift box composite — 156×85, with children extending
                beyond the bounding box (negative top/right values). */}
            <div class="relative h-[85px] max-h-[98.08px] min-h-[65.38px] w-[156px] min-w-[120px] max-w-[180px] shrink-0">
              {/* Main object — new sheet PNG has natural aspect 576/452. Use
                  fixed width only so the browser preserves the natural ratio
                  (no horizontal stretch). Vertically centered on the gift-box
                  container so the larger natural height extends evenly. */}
              <img
                src={objectPng}
                alt=""
                aria-hidden="true"
                class="absolute left-1/2 top-[calc(50%-28px)] w-[175px] max-w-none -translate-x-1/2 -translate-y-1/2 scale-[0.9] max-[374px]:scale-[0.85] min-[413px]:scale-[0.95]"
              />
              {/* Ellipse 3 glow — plus-lighter, 87×41 with -20.51%/-9.67%
                  bleed (so true painted size is ~96×51, centered on the
                  87×41 box at left:34.5/top:14). */}
              <div
                aria-hidden
                class="pointer-events-none absolute left-[34.5px] top-[14px] h-[41px] w-[87px] mix-blend-plus-lighter"
              >
                <img
                  src={ellipse3Svg}
                  alt=""
                  aria-hidden="true"
                  class="absolute -inset-x-[9.67%] -inset-y-[20.51%] block size-full max-w-none"
                />
              </div>

              {/* Dots cluster (top-left) — plus-lighter, rotated -6.46deg. */}
              <div
                aria-hidden
                class="pointer-events-none absolute left-[15.45px] top-[-1.51px] flex h-[43.141px] w-[58.679px] items-center justify-center mix-blend-plus-lighter"
              >
                <img
                  src={dots1Svg}
                  alt=""
                  aria-hidden="true"
                  class="h-[37.208px] w-[54.841px] [transform:rotate(-6.46deg)]"
                />
              </div>
              {/* Dots cluster (top-right) — plus-lighter, rotated -115.74deg. */}
              <div
                aria-hidden
                class="pointer-events-none absolute left-[84.63px] top-[-30.12px] flex h-[34.115px] w-[34.881px] items-center justify-center mix-blend-plus-lighter"
              >
                <img
                  src={dots2Svg}
                  alt=""
                  aria-hidden="true"
                  class="h-[26.66px] w-[25.019px] [transform:rotate(-115.74deg)]"
                />
              </div>
              {/* Diamond lens-flare reflection. Two cross instances on
                  adjacent facets of the gem decoration (container-relative
                  ~129,61 derived from Figma node 4535:40007 position).
                  Horizontal + vertical gradient bars form a cross; diagonal
                  bars add extra glint. mix-blend-plus-lighter keeps them
                  additive over the illustration. */}
              {/* Diamond lens-flare on the main gem cluster (centre of the
                  cyan crystals inside the box). Container-relative coords
                  derived from screenshot: image top-left ≈ (−9.5,−47.9)
                  in container space; gem cluster centre ≈ (79,46) in the
                  175×133 image → container (70,−2). */}
              <div
                aria-hidden
                class="pointer-events-none absolute left-[80px] top-[-10px] mix-blend-plus-lighter [animation:lightRayFlash_3.6s_ease-in-out_infinite]"
              >
                <div class="absolute h-[2px] w-[28px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-white to-transparent" />
                <div class="absolute h-[24px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-transparent via-white to-transparent" />
                <div class="absolute h-[1.5px] w-[18px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                <div class="absolute h-[1.5px] w-[18px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/60 to-transparent" />
              </div>
              {/* Second facet — offset to another gem, fires out-of-phase. */}
              <div
                aria-hidden
                class="pointer-events-none absolute left-[54px] top-[30px] mix-blend-plus-lighter [animation:lightRayFlash_3.2s_ease-in-out_infinite_1.8s]"
              >
                <div class="absolute h-[1.5px] w-[20px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-transparent via-white to-transparent" />
                <div class="absolute h-[18px] w-[1.5px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-transparent via-white to-transparent" />
                <div class="absolute h-[1px] w-[14px] -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent" />
                <div class="absolute h-[1px] w-[14px] -translate-x-1/2 -translate-y-1/2 -rotate-45 rounded-full bg-gradient-to-r from-transparent via-white/50 to-transparent" />
              </div>
            </div>

            {/* (e) Message container — flex-1, gap-3, pb-4 per Figma. */}
            <div class="relative flex min-w-[150px] flex-[1_0_0] items-center gap-3 pb-4">
              <div class="flex min-w-0 flex-[1_0_0] items-center gap-2">
                <img
                  src={starSvg}
                  alt=""
                  aria-hidden="true"
                  class="size-[13px] shrink-0"
                />
                <p class="min-w-0 flex-[1_0_0] text-[16px] font-semibold leading-[22px] text-white [text-shadow:0_0_8.4px_rgba(187,81,253,0.64)]">
                  {m.booster_banner_title()}
                </p>
              </div>
              <span
                role="button"
                tabindex="0"
                onClick={handleClose}
                aria-label="Dismiss"
                class="pointer-events-auto grid size-5 shrink-0 cursor-pointer place-items-center"
              >
                <img src={closeSvg} alt="" aria-hidden="true" class="size-5" />
              </span>
            </div>
          </button>
        </div>
      </div>
    </Show>
  )
}
